import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * The interface language switch.
 *
 * Mirrors the {@link ThemeToggle} idiom — a labelled radio group — so the two controls sit
 * side by side as one family. The choice is persisted by the i18next language detector
 * (localStorage), so it survives a reload without any code here having to store it.
 */

const OPTIONS: { value: 'en' | 'th'; label: string }[] = [
  { value: 'en', label: 'EN' },
  { value: 'th', label: 'ไทย' },
];

const ARIA_LABELS: Record<'en' | 'th', string> = { en: 'English', th: 'ไทย' };

/** A two-way language switch, exposed as a labelled radio group for keyboard and AT users. */
export function LanguageSwitcher({ className }: { className?: string | undefined }): ReactNode {
  const { i18n } = useTranslation();
  const current = i18n.resolvedLanguage ?? i18n.language;
  return (
    <div
      role="radiogroup"
      aria-label="Language"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-lg border border-border p-0.5',
        className,
      )}
    >
      {OPTIONS.map(({ value, label }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={current === value}
          aria-label={ARIA_LABELS[value]}
          title={ARIA_LABELS[value]}
          onClick={() => {
            void i18n.changeLanguage(value);
          }}
          className={cn(
            'flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            current === value
              ? 'bg-accent text-accent-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
